import React, { useEffect, useState, type FC } from "react";
import { Alert, Select, Spin, Typography, message } from "antd";
import { Modal } from "../../../../components/shared";
import { useDebouncedValue } from "../../../../hooks/useDebouncedSearchQuery";
import { usePamFeature } from "../../../../hooks/useFeatureFlag";
import { useTestSendCampaign, type TestSendResult } from "../../../../queries/dynamicBonuses/useTestSendCampaign";
import { useTestSendDraftCampaign } from "../../../../queries/dynamicBonuses/useTestSendDraftCampaign";
import {
  isForbiddenError,
  useSearchTestSendUsers,
} from "../../../../queries/dynamicBonuses/useSearchTestSendUsers";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";
import type { CampaignIn } from "../types";

const SEARCH_DEBOUNCE_MS = 300;

type SendTestModalProps = {
  open: boolean;
  onClose: () => void;
  campaignId?: string;
  campaign?: CampaignIn;
};

const SendTestModal: FC<SendTestModalProps> = ({
  open,
  onClose,
  campaignId,
  campaign,
}) => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [invalid, setInvalid] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [tagsOnly, setTagsOnly] = useState(false);
  const [searchUnavailable, setSearchUnavailable] = useState(false);
  const persistedMutation = useTestSendCampaign(campaignId ?? "");
  const draftMutation = useTestSendDraftCampaign();
  const mutation = campaign ? draftMutation : persistedMutation;
  const { isEnabled: multiStepEnabled, loading: multiStepLoading } =
    usePamFeature("multiStepCampaignTestSend");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const usersQuery = useSearchTestSendUsers(debouncedSearch, open && !tagsOnly);

  useEffect(() => {
    if (!usersQuery.isError) return;
    setTagsOnly(true);
    setSearchUnavailable(!isForbiddenError(usersQuery.error));
  }, [usersQuery.isError, usersQuery.error]);

  const reset = () => {
    setRecipients([]);
    setInvalid([]);
    setSearch("");
    setTagsOnly(false);
    setSearchUnavailable(false);
    persistedMutation.reset();
    draftMutation.reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleResult = (result: TestSendResult) => {
    if (result.invalid_recipients.length > 0) {
      setInvalid(result.invalid_recipients);
      return;
    }
    if (result.sent_count === 0) {
      message.warning(AppStrings.PAM_DYNAMIC_BONUS_TEST_NOT_SENT);
      return;
    }
    // Every recipient receives the same steps, so the first entry is representative.
    // A backend without the BONENG-1093 payload sends no `steps`, which still means
    // one step was delivered — never report zero.
    const stepCount = result.recipients[0]?.steps?.length || 1;
    message.success(
      formatString(
        result.sent_count === 1
          ? AppStrings.PAM_DYNAMIC_BONUS_TEST_SENT_SINGULAR
          : AppStrings.PAM_DYNAMIC_BONUS_TEST_SENT_PLURAL,
        {
          count: String(result.sent_count),
          steps:
            stepCount === 1
              ? AppStrings.PAM_DYNAMIC_BONUS_TEST_SENT_STEPS_SINGULAR
              : formatString(AppStrings.PAM_DYNAMIC_BONUS_TEST_SENT_STEPS_PLURAL, {
                  count: String(stepCount),
                }),
          recipients: result.recipients.map((r) => r.recipient).join(", "),
        },
      ),
    );
    handleClose();
  };

  const handleSend = () => {
    const cleaned = Array.from(
      new Set(recipients.map((r) => r.trim()).filter(Boolean)),
    );
    if (cleaned.length === 0) {
      message.warning(AppStrings.PAM_DYNAMIC_BONUS_TEST_RECIPIENT_REQUIRED);
      return;
    }
    setInvalid([]);
    const callbacks = {
      onSuccess: handleResult,
      onError: (error: Error) => {
        message.error(
          getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED),
        );
      },
    };
    if (campaign) {
      draftMutation.mutate({ campaign, recipients: cleaned }, callbacks);
      return;
    }
    persistedMutation.mutate(cleaned, callbacks);
  };

  const options = (usersQuery.data ?? []).map((user) => ({
    value: user.username,
    label: user.username,
  }));

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={AppStrings.PAM_DYNAMIC_BONUS_SEND_TEST}
      size="md"
      primaryAction={{
        label: AppStrings.PAM_DYNAMIC_BONUS_SEND_TEST,
        onClick: handleSend,
        loading: mutation.isPending,
        disabled: recipients.length === 0,
      }}
      secondaryAction={{ label: AppStrings.CANCEL, onClick: handleClose }}
      destroyOnClose
    >
      {/* Rendering before the flag resolves would show the first-step copy and
          then swap it, so stay blank for the one frame it takes to load. */}
      {!multiStepLoading && (
        <Typography.Paragraph type="secondary">
          {multiStepEnabled
            ? AppStrings.PAM_DYNAMIC_BONUS_TEST_SEND_DESCRIPTION_ALL_STEPS
            : AppStrings.PAM_DYNAMIC_BONUS_TEST_SEND_DESCRIPTION}
        </Typography.Paragraph>
      )}

      {/* A test send is not a dry run in either mode: AWARD steps move real value,
          and running against a fresh shadow progress row means max_awards_per_user
          never caps it. Deliberately not gated on the flag — the description above
          says a test "doesn't affect the real audience", which reads as nothing
          real happening, and that was just as misleading before the fan-out.
          Static copy rather than an Alert, so it neither claims the `alert` role
          the transient errors below use nor announces on every open. */}
      <Typography.Paragraph type="warning">
        {AppStrings.PAM_DYNAMIC_BONUS_TEST_SEND_AWARD_WARNING}
      </Typography.Paragraph>

      {searchUnavailable && (
        <Alert
          style={{ marginBottom: 12 }}
          type="warning"
          showIcon
          message={AppStrings.PAM_DYNAMIC_BONUS_TEST_SEARCH_UNAVAILABLE}
        />
      )}
      <Select
        mode="tags"
        style={{ width: "100%" }}
        placeholder={AppStrings.PAM_DYNAMIC_BONUS_TEST_RECIPIENTS_PLACEHOLDER}
        value={recipients}
        onChange={(next) => {
          setRecipients(next);
          if (invalid.length > 0) setInvalid([]);
        }}
        onSearch={setSearch}
        filterOption={false}
        tokenSeparators={[",", " "]}
        options={tagsOnly ? undefined : options}
        notFoundContent={usersQuery.isFetching ? <Spin size="small" /> : null}
        aria-label={AppStrings.PAM_DYNAMIC_BONUS_TEST_RECIPIENTS_ARIA}
      />

      {invalid.length > 0 && (
        <Alert
          style={{ marginTop: 12 }}
          type="error"
          showIcon
          message={AppStrings.PAM_DYNAMIC_BONUS_TEST_INVALID_RECIPIENTS_TITLE}
          description={
            <>
              {AppStrings.PAM_DYNAMIC_BONUS_TEST_INVALID_RECIPIENTS_BODY}
              <div style={{ marginTop: 4, fontWeight: 600 }}>{invalid.join(", ")}</div>
            </>
          }
        />
      )}
    </Modal>
  );
};

export default SendTestModal;
